import { useMemo, useState } from 'react'
import zipcodes from 'zipcodes'
import { useNavigate } from '@/router/navigation'
import { useLocations, useNearbyLocations, useSubmitCallRequest } from '@/api/enrollment-api'
import {
  getCallRequestErrorMessage,
  getCallRequestZipFromStorage,
  getInitialCallRequestForm,
  toCallRequestLocationOption,
  validateCallRequestForm
} from './helpers'
import type { CallRequestForm } from './types'

export function useCallRequestPage() {
  const navigate = useNavigate()
  const submitCallRequest = useSubmitCallRequest()
  const [form, setForm] = useState<CallRequestForm>(getInitialCallRequestForm)
  const [errors, setErrors] = useState<Record<string, string | undefined>>({})
  const [submitError, setSubmitError] = useState<string | null>(null)

  const zip = getCallRequestZipFromStorage()
  const zipInfo = zip ? zipcodes.lookup(zip) : null
  const { data: nearbyLocations, isLoading: locationsLoading } = useNearbyLocations(
    zipInfo?.latitude ?? null,
    zipInfo?.longitude ?? null
  )
  const { data: allLocations, isLoading: allLocationsLoading } = useLocations()

  const nearbyBranchLocations = useMemo(
    () => (nearbyLocations ?? []).map(({ location }) => location),
    [nearbyLocations]
  )
  const allBranchLocations = useMemo(
    () => (allLocations ?? []).filter(location => location.active),
    [allLocations]
  )
  const branchLocations =
    nearbyBranchLocations.length > 0 ? nearbyBranchLocations : allBranchLocations
  const locationOptions = useMemo(
    () => branchLocations.map(toCallRequestLocationOption),
    [branchLocations]
  )
  const branchesLoading =
    locationsLoading || (nearbyBranchLocations.length === 0 && allLocationsLoading)

  const update = (field: keyof CallRequestForm, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }))
    setErrors(prev => ({ ...prev, [field]: undefined }))
  }

  const submit = async () => {
    const nextErrors = validateCallRequestForm(form)
    setErrors(nextErrors)

    if (Object.keys(nextErrors).length > 0) {
      return
    }

    setSubmitError(null)

    try {
      await submitCallRequest.mutateAsync({
        trainingLocationExternalId: form.trainingLocationExternalId || undefined,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        preferredCallTime: form.preferredCallTime.trim() || undefined,
        notes: form.notes.trim() || undefined
      })

      navigate('/thank-you', { state: { callRequest: true } })
    } catch (error) {
      setSubmitError(getCallRequestErrorMessage(error))
    }
  }

  return {
    branchesLoading,
    errors,
    form,
    locationOptions,
    navigate,
    submit,
    submitCallRequest,
    submitError,
    setSubmitError,
    update
  }
}
